import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/flowly/infra/Button/Button";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { CANCELLATION_REASONS } from "./constants";
import type { CancellationReason, UserCancellationReason } from "./types";

// `service_shutdown` is an operational reason set by the wind-down script, never
// shown in the user-facing reason picker — so it's excluded from this label map.
const REASON_TRANSLATION_KEYS: Record<UserCancellationReason, string> = {
  too_expensive: "reasonTooExpensive",
  not_using_enough: "reasonNotUsing",
  missing_features: "reasonMissingFeatures",
  switching_competitor: "reasonSwitching",
  quality_not_good: "reasonQuality",
  too_complicated: "reasonComplicated",
  other: "reasonOther",
};

interface ReasonStepProps {
  selectedReason: CancellationReason | null;
  onReasonChange: (reason: CancellationReason) => void;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  onContinue: () => void;
}

export const ReasonStep = ({
  selectedReason,
  onReasonChange,
  feedback,
  onFeedbackChange,
  onContinue,
}: ReasonStepProps) => {
  const { t, isRTL } = useLanguage();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          {t.cancellation.whyCancelling}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t.cancellation.whyCancellingSubtitle}
        </p>
      </div>

      <RadioGroup
        value={selectedReason ?? ""}
        onValueChange={(val) => onReasonChange(val as CancellationReason)}
        className="space-y-2"
      >
        {CANCELLATION_REASONS.map((reason) => (
          <div
            key={reason}
            className={cn(
              "flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 hover:bg-muted/50 transition-colors cursor-pointer",
              isRTL && "flex-row-reverse"
            )}
            onClick={() => onReasonChange(reason)}
          >
            <RadioGroupItem value={reason} id={reason} />
            <Label htmlFor={reason} className={cn("text-sm cursor-pointer flex-1", isRTL && "text-right")}>
              {t.cancellation[REASON_TRANSLATION_KEYS[reason] as keyof typeof t.cancellation]}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {/* Free-text feedback — shown for all reasons */}
      {selectedReason && (
        <Textarea
          value={feedback}
          onChange={(e) => onFeedbackChange(e.target.value)}
          placeholder={
            selectedReason === "other"
              ? t.cancellation.otherPlaceholder
              : t.cancellation.feedbackPlaceholder
          }
          className="text-sm"
          rows={2}
        />
      )}

      <Button
        variant="primary"
        className="w-full"
        onClick={onContinue}
        disabled={!selectedReason}
      >
        {t.cancellation.continueButton}
      </Button>
    </div>
  );
};
